import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Virtualizer, observeElementOffset, observeElementRect, elementScroll } from '@tanstack/virtual-core';
import type { BackendClient } from '../../bridge/backend-client';
import type { CatalogRow, SymbolPageData } from '../../../core/bridge/connect-client';
import type { StreamFrame } from '../../../core/bridge/types';
import { parseSpec, methodColor } from './endpoint-method';
import { SEARCH_DEBOUNCE_MS } from '../../../core/ui-constants';

/*
 * <vitesse-endpoint-op-list> — the LEFT pane of the Postman-shaped endpoint
 * viewport: the operations list (the collection sidebar). Bespoke on purpose —
 * REST ops are not PLC symbols, so this is NOT the SymbolGrid. It is a virtualized
 * (virtual-core, the log-tree pattern) list over the integration schema, paged
 * on demand so it scales to tens of thousands of ops with an O(window) DOM. The
 * window is a STRUCTURE STREAM (the same push contract the topic screen uses):
 * the indexer sends the page once, then again whenever the catalog changes
 * (server-side hash-compared) — so an added/removed/rediscovered operation
 * appears the moment ingest finishes, with zero polling. Rows render by `kind`:
 *   • endpoint → an invocable op: coloured METHOD badge (from `detail`) + name.
 *   • object   → a container value (a whole response/record), foldable.
 *   • field    → a leaf value under an op (shown indented; its live value/sparkline
 *                lands in the value-plane pass).
 * Selecting an op emits `op-selected` (detail+path+writable) so the builder pane
 * loads it. This pane owns NO builder state — it's a pure selector over the schema.
 */

const ROW_H = 40;
const WINDOW = 400; // rows fetched per page; the loaded slice slides with the viewport
const OVERSCAN = 8;
/** Rows leased either side of the visible range. The backend produces values
 *  ONLY for leased paths, and each one is a real external poll against the
 *  customer's API — so the lease is the on-screen slice plus this pad, never the
 *  whole 400-row page. */
const LEASE_PAD = 6;
/** Floor between lease re-scopes: one tagged fetch per window at most, never
 *  one per scroll tick. */
const RESCOPE_MIN_MS = 150;

interface OpSelected {
  path: string;
  detail: string;
  kind: string;
  writable: boolean;
}

@customElement('vitesse-endpoint-op-list')
export class EndpointOpList extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() integrationId = '';
  /** The view's window-follow tag. Every page fetch carries it so the api-gateway
   *  re-scopes the value stream to this window AND extends the edge observe-lease
   *  to its leaves — an integration value is produced ONLY while observed, so
   *  without this tag no row ever gets a value. */
  @property() clientSession = '';
  /** path → display value, fed by the viewport's window-value stream. The SAME
   *  Map identity across frames (the viewport's model mutates in place, copying
   *  nothing); `valuesVersion` is what signals a change. */
  @property({ attribute: false }) values: ReadonlyMap<string, string> = new Map();
  @property({ type: Number }) valuesVersion = 0;

  @state() private total = 0;
  @state() private windowStart = 0; // absolute index of rows[0]
  @state() private rows: CatalogRow[] = [];
  @state() private selectedPath = '';
  @state() private search = '';
  @state() private loading = false;

  private virtualizer: Virtualizer<HTMLElement, Element> | null = null;
  private virtualizerCleanup: (() => void) | null = null;
  private scrollEl: HTMLElement | null = null;
  private searchTimer?: number;
  private inflight = 0; // monotonic stream id — a superseded stream's frames are dropped
  private structurePort: MessagePort | null = null; // the open window stream
  private loadedFor = ''; // integrationId+search the current window belongs to
  private pendingTarget = -1; // offset of the page in flight — a re-ask for it is a no-op
  private seededFor = ''; // the integration the first-row seed already fired for
  /** Absolute row range the value lease currently covers (see maybeRescope). */
  private leased: { start: number; end: number } | null = null;
  private rescopeTimer?: number;
  private lastRescope = 0;

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 360px;
      flex: none;
      min-height: 0;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      overflow: hidden;
      background: var(--bg-primary);
    }
    .search {
      flex: none;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-bottom: 1px solid var(--border-color);
      background: var(--tree-bg);
    }
    .search svg {
      color: var(--text-secondary);
      flex: none;
    }
    .search input {
      flex: 1;
      min-width: 0;
      background: transparent;
      border: none;
      outline: none;
      color: var(--text-primary);
      font-size: 13px;
      font-family: inherit;
    }
    .scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      position: relative;
    }
    .spacer {
      position: relative;
      width: 100%;
    }
    .row {
      position: absolute;
      left: 0;
      right: 0;
      display: flex;
      align-items: center;
      gap: 9px;
      height: ${ROW_H}px;
      padding: 0 12px;
      box-sizing: border-box;
      cursor: pointer;
      border-bottom: 1px solid var(--border-subtle, var(--border-color-light));
    }
    .row:hover {
      background: var(--hover-bg);
    }
    .row.sel {
      background: var(--selected-bg, color-mix(in srgb, var(--blue) 12%, transparent));
      box-shadow: inset 2px 0 0 var(--blue);
    }
    .row.field {
      padding-left: 30px;
    }
    .method {
      flex: none;
      font: 700 10px/1 'SF Mono', ui-monospace, Menlo, monospace;
      letter-spacing: 0.03em;
      padding: 2px 6px;
      border-radius: 4px;
    }
    .obj-ico {
      flex: none;
      color: var(--text-tertiary);
      display: flex;
    }
    .name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      color: var(--text-primary);
    }
    .name .path {
      font-size: 11px;
      color: var(--text-tertiary);
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .val {
      flex: none;
      max-width: 45%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      font-size: 11.5px;
      color: var(--blue-light, var(--blue));
    }
    .wtag {
      flex: none;
      font-size: 10px;
      color: var(--amber);
      border: 1px solid color-mix(in srgb, var(--amber) 40%, transparent);
      border-radius: 4px;
      padding: 1px 6px;
    }
    .skel {
      position: absolute;
      left: 12px;
      right: 12px;
      height: 12px;
      top: ${(ROW_H - 12) / 2}px;
      border-radius: 4px;
      background: var(--hover-bg);
      opacity: 0.5;
    }
    .empty {
      padding: 28px 14px;
      text-align: center;
      color: var(--text-secondary);
      font-size: 13px;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.rescopeTimer !== undefined) clearTimeout(this.rescopeTimer);
    this.closeStream();
    this.virtualizerCleanup?.(); // detaches the scroll/resize observers
    this.virtualizerCleanup = null;
    this.virtualizer = null;
  }

  protected firstUpdated(): void {
    this.scrollEl = this.renderRoot.querySelector<HTMLElement>('.scroll');
    this.setupVirtualizer();
    // No reload here: updated() runs right after with integrationId in `changed`
    // (its first set), so loading here too meant two fetches per mount.
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('integrationId') || changed.has('gatewayId')) {
      this.seededFor = ''; // a new integration gets its own one-time seed
      this.reload();
    }
  }

  private setupVirtualizer(): void {
    if (!this.scrollEl) return;
    this.virtualizer = new Virtualizer<HTMLElement, Element>({
      count: this.total,
      getScrollElement: () => this.scrollEl,
      estimateSize: () => ROW_H,
      overscan: OVERSCAN,
      observeElementOffset,
      observeElementRect,
      scrollToFn: elementScroll,
      onChange: () => {
        this.ensureWindow();
        this.requestUpdate();
      },
    });
    // Must call _didMount (returns the observer cleanup) + _willUpdate to start
    // scroll observation — same as SymbolGrid.
    this.virtualizerCleanup = this.virtualizer._didMount();
    this.virtualizer._willUpdate();
  }

  /** Fresh load (mount / integration change / search change): reset the window,
   *  fetch from the top, learn the total. */
  private reload(): void {
    if (!this.backend || !this.integrationId) return;
    const key = `${this.integrationId}\0${this.search}`;
    this.loadedFor = key;
    this.windowStart = 0;
    this.rows = [];
    this.selectedPath = '';
    this.openWindow(0, key);
    this.scrollEl?.scrollTo({ top: 0 });
  }

  /** Ensure the visible virtual range is covered by the loaded window; if the user
   *  scrolled to its edge, slide the window (one page fetch). Then keep the value
   *  lease on the on-screen slice. Runs on every virtualizer change — so it MUST
   *  be a no-op at rest, and is. */
  private ensureWindow(): void {
    if (!this.virtualizer) return;
    const items = this.virtualizer.getVirtualItems();
    if (items.length === 0) return;
    const first = items[0].index;
    const last = items[items.length - 1].index;
    const haveStart = this.windowStart;
    const haveEnd = this.windowStart + this.rows.length;
    // Near an edge that has rows BEYOND it. The list's own ends don't count —
    // there is nothing past them to fetch. (The old check counted them, so at the
    // top or bottom it was permanently true and refetched the same page forever.)
    const nearTop = first < haveStart + OVERSCAN && haveStart > 0;
    const nearBottom = last > haveEnd - OVERSCAN && haveEnd < this.total;
    if (nearTop || nearBottom) {
      // CENTRE the window on the visible range (was a quarter-window behind it,
      // so scrolling up ran out of buffer at once), clamped to the list's ends.
      const visible = last - first + 1;
      const centred = first - Math.floor((WINDOW - visible) / 2);
      const target = Math.max(0, Math.min(centred, Math.max(0, this.total - WINDOW)));
      // Only if the window would actually MOVE, and not for a page already on
      // its way. This is the "zero requests at rest" guarantee.
      if (target !== this.windowStart && !(this.loading && target === this.pendingTarget)) {
        this.openWindow(target, this.loadedFor);
      }
    }
    this.maybeRescope(first, last);
  }

  /** OPERATIONS ONLY. A flattened response leaf (`live_co2/readings/zone0/field0`)
   *  is not a request — it's produced BY one — so it belongs in the response
   *  viewer, not the collection sidebar (Postman lists requests, never a
   *  response's fields). Filtered server-side: a catalog can hold 80k ops and
   *  far more leaves, so paging everything to drop most of it client-side would
   *  break both paging and the row budget. */
  private ftsQuery(): string {
    return `/kind=endpoint${this.search ? ` ${this.search}` : ''}`;
  }

  private closeStream(): void {
    if (!this.structurePort) return;
    this.structurePort.postMessage({ kind: 'close' }); // the worker tears the source down
    this.structurePort.close();
    this.structurePort = null;
  }

  /** Open the STRUCTURE STREAM for the window at `offset` — untagged: it only
   *  pages; the value lease rides the tagged twin in maybeRescope. A newer window
   *  supersedes this one (seq guard) and closes its port. The stream pushes the
   *  snapshot page, then every recomputed page on catalog change — no polling. */
  private openWindow(offset: number, key: string): void {
    if (!this.backend) return;
    const id = ++this.inflight;
    this.pendingTarget = offset;
    this.loading = true;
    this.closeStream();
    void (async () => {
      try {
        const port = await this.backend!.api.subscribeWindowSymbolPage(
          this.gatewayId,
          this.integrationId,
          { offset, pageSize: WINDOW, fts: this.ftsQuery() },
          true,
        );
        if (!port) return;
        if (id !== this.inflight || key !== this.loadedFor) {
          port.postMessage({ kind: 'close' }); // superseded mid-await
          port.close();
          return;
        }
        port.onmessage = (e: MessageEvent<StreamFrame<SymbolPageData>>) => {
          if (id !== this.inflight || key !== this.loadedFor) return; // superseded
          const frame = e.data;
          if (frame.kind !== 'snapshot') return; // 'error' → keep the last page (the worker reconnects)
          const page = frame.items[0];
          if (page) this.applyPage(page, offset);
        };
        port.start();
        this.structurePort = port;
      } catch {
        /* the worker supervises the source; the next scroll re-opens */
        if (id === this.inflight) {
          this.loading = false;
          this.pendingTarget = -1;
        }
      }
    })();
  }

  /** One COMPLETE window page (snapshot-shaped — replace, not append): the first
   *  after open, and one per catalog change after that. */
  private applyPage(page: SymbolPageData, offset: number): void {
    this.total = page.totalMatching;
    this.windowStart = offset;
    this.rows = page.rows;
    this.loading = false;
    this.pendingTarget = -1;
    this.leased = null; // rows changed → the lease must be re-scoped onto them
    // Seed the pane so the view never opens empty — ONCE per integration. A
    // search or a reconnect also lands page zero, and must not yank the reader
    // off the op they chose. (The viewport additionally ignores the seed when
    // it restored a remembered op.)
    if (offset === 0 && page.rows.length > 0 && this.seededFor !== this.integrationId) {
      this.seededFor = this.integrationId;
      const r = page.rows[0];
      this.dispatchEvent(
        new CustomEvent<OpSelected>('first-row', {
          detail: { path: r.path, detail: r.detail, kind: r.kind, writable: r.permission.includes('w') },
          bubbles: true,
          composed: true,
        }),
      );
    }
    if (this.virtualizer) {
      this.virtualizer.setOptions({ ...this.virtualizer.options, count: this.total });
      this.virtualizer._willUpdate();
      const items = this.virtualizer.getVirtualItems();
      if (items.length) this.maybeRescope(items[0].index, items[items.length - 1].index);
    }
  }

  /** Lease only what's on screen. The gateway honours a viewport hint on
   *  integration page fetches — anchor row path + size — leasing just that slice
   *  instead of the whole page (an unknown anchor falls back to the page, so a
   *  stale hint can never lease LESS than before). This is a tagged twin of the
   *  current page query (same offset/size/filter, so the anchor is in its rows),
   *  the same trick the topic screen uses after a prefetch hit. Sent only when
   *  the visible range leaves the leased slice, throttled — at rest, nothing. */
  private maybeRescope(first: number, last: number): void {
    if (!this.clientSession || !this.backend || this.rows.length === 0) return;
    if (this.leased && first >= this.leased.start && last <= this.leased.end) return;
    const now = performance.now();
    const wait = RESCOPE_MIN_MS - (now - this.lastRescope);
    if (wait > 0) {
      if (this.rescopeTimer === undefined) {
        this.rescopeTimer = window.setTimeout(() => {
          this.rescopeTimer = undefined;
          const items = this.virtualizer?.getVirtualItems() ?? [];
          if (items.length) this.maybeRescope(items[0].index, items[items.length - 1].index);
        }, wait);
      }
      return;
    }
    const start = Math.max(this.windowStart, first - LEASE_PAD);
    const end = Math.min(this.windowStart + this.rows.length - 1, last + LEASE_PAD);
    const anchor = this.rows[start - this.windowStart];
    if (!anchor || end < start) return;
    this.lastRescope = now;
    this.leased = { start, end };
    void this.backend.api
      .fetchIntegrationPage(this.gatewayId, this.integrationId, {
        offset: this.windowStart,
        pageSize: WINDOW,
        fts: this.ftsQuery(),
        clientSession: this.clientSession,
        viewportAnchorPath: anchor.path,
        viewportSize: end - start + 1,
      })
      .catch(() => {}); // best-effort — values are not load-bearing for the list
  }

  private onSearch(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.search = v;
      void this.reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  private select(r: CatalogRow): void {
    this.selectedPath = r.path;
    this.dispatchEvent(
      new CustomEvent<OpSelected>('op-selected', {
        detail: { path: r.path, detail: r.detail, kind: r.kind, writable: r.permission.includes('w') },
        bubbles: true,
        composed: true,
      }),
    );
  }

  protected render() {
    const v = this.virtualizer;
    const items = v?.getVirtualItems() ?? [];
    const totalH = v?.getTotalSize() ?? 0;
    return html`
      <div class="search">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input type="text" placeholder="Search operations…" @input=${(e: Event) => this.onSearch(e)} />
      </div>
      <div class="scroll">
        ${this.total === 0 && !this.loading
          ? html`<div class="empty">No operations.</div>`
          : html`<div class="spacer" style="height:${totalH}px">
              ${items.map((vi) => this.renderRow(vi.index, vi.start))}
            </div>`}
      </div>
    `;
  }

  private renderRow(index: number, top: number) {
    const r = this.rows[index - this.windowStart];
    if (!r) {
      return html`<div class="row" style="transform:translateY(${top}px)"><div class="skel"></div></div>`;
    }
    const leaf = r.path.slice(Math.max(r.path.lastIndexOf('/'), r.path.lastIndexOf('.')) + 1);
    const sel = r.path === this.selectedPath ? 'sel' : '';
    if (r.kind === 'endpoint') {
      const { method, url } = parseSpec(r.detail);
      const [fg, bg] = methodColor(method);
      const write = r.permission.includes('w') && !r.permission.includes('r');
      return html`<div class="row ${sel}" style="transform:translateY(${top}px)" @click=${() => this.select(r)}>
        <span class="method" style="color:${fg};background:${bg}">${method}</span>
        <div class="name">${leaf}<div class="path">${url}</div></div>
        ${write ? html`<span class="wtag">write</span>` : nothing}
      </div>`;
    }
    if (r.kind === 'object') {
      return html`<div class="row ${sel}" style="transform:translateY(${top}px)" @click=${() => this.select(r)}>
        <span class="obj-ico"
          ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 7V5a2 2 0 0 1 2-2h2M4 17v2a2 2 0 0 0 2 2h2M20 7V5a2 2 0 0 0-2-2h-2M20 17v2a2 2 0 0 1-2 2h-2" /></svg
        ></span>
        <div class="name">${leaf}</div>
      </div>`;
    }
    // field leaf — its live value arrives on the window-follow stream (produced
    // only while the row is in the leased window, hence the clientSession tag).
    const val = this.values.get(r.path);
    return html`<div class="row field ${sel}" style="transform:translateY(${top}px)" @click=${() => this.select(r)}>
      <div class="name">${leaf}</div>
      ${val === undefined ? nothing : html`<span class="val" title=${val}>${val}</span>`}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-endpoint-op-list': EndpointOpList;
  }
}
